import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTheme } from '../../Context/ThemeContext';
import './index.css';
import './styles.css';


// Helper function to format keys (e.g., "total_invoices" to "Total Invoices")
const formatKey = (key) => {
    return key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

const ResponseItem = ({ item }) => {
    const { isDarkMode } = useTheme();
    const [previewImg, setPreviewImg] = useState(null);

    let parsedData;
    let queryResponseContent = '';
    let statisticsElements = null;

    try {
        // Ensure item.answer is parsed if it's a string
        if (typeof item.answer === 'string') {
            const trimmed = item.answer.trim();
            // Only attempt JSON.parse if it looks like JSON
            const looksLikeJson =
                (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
                (trimmed.startsWith('[') && trimmed.endsWith(']'));
            if (looksLikeJson) {
                parsedData = JSON.parse(trimmed);
            } else {
                // Treat as plain text (e.g., placeholders like "thinking...")
                queryResponseContent = item.answer;
            }
        } else if (typeof item.answer === 'object' && item.answer !== null) {
            parsedData = item.answer; // It's already an object
        } else {
            // Handle cases where item.answer might be undefined or not what we expect
            console.warn("item.answer is not a parsable string or object:", item.answer);
            queryResponseContent = "Invalid answer format.";
        }

        if (parsedData) {
            // Get the query_response string for ReactMarkdown
            if (parsedData.query_response) {
                queryResponseContent = parsedData.query_response;
            } else if (typeof parsedData === 'string') {
                // If the entire parsedData is just a string (e.g. simple text response)
                queryResponseContent = parsedData;
            }


            // Prepare statistics for display
            if (parsedData.statistics && typeof parsedData.statistics === 'object') {
                statisticsElements = (
                    <div className={`statistics-container ${isDarkMode ? 'dark-mode' : ''}`}>
                        <h5>Statistics:</h5>
                        <ul>
                            {Object.entries(parsedData.statistics).map(([key, value]) => {
                                if (key === "reason_codes" && typeof value === 'object' && value !== null) {
                                    return (
                                        <li key={key}>
                                            <strong>{formatKey(key)}:</strong>
                                            <ul className="reason-codes-list">
                                                {Object.entries(value).map(([rKey, rValue]) => (
                                                    <li key={rKey}>
                                                        {formatKey(rKey)}: {(Number(rValue) * 100).toFixed(1)}%
                                                    </li>
                                                ))}
                                            </ul>
                                        </li>
                                    );
                                }else if (key === "random_forest_analysis" && typeof value === 'object' && value !== null) {
                                    return (
                                        <li key={key}>
                                            <strong>{formatKey(key)}:</strong>
                                            <ul className="sub-details-list"> {/* Use a general class for nested details */}
                                                {Object.entries(value).map(([rfKey, rfValue]) => (
                                                    <li key={rfKey}>
                                                        {formatKey(rfKey)}: {String(rfValue)}
                                                    </li>
                                                ))}
                                            </ul>
                                        </li>
                                    );
                                }

                                return (
                                    <li key={key}>
                                        <strong>{formatKey(key)}:</strong> {
                                            typeof value === 'number'
                                                ? (key.toLowerCase().includes('value') ? '$' : '') + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                : String(value) // Ensure value is a string or number
                                        }
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                );
            }
        }
    } catch (error) {
        error("Error parsing or processing item.answer:", error, "Raw answer:", item.answer);
        // If parsing fails, try to display item.answer as a string if possible,
        // or show an error message.
        queryResponseContent = typeof item.answer === 'string' ? item.answer : "Error displaying answer content.";
    }

    return (
        <div className={`response-item ${isDarkMode ? 'dark-mode' : ''}`}>
            <div className={`question ${isDarkMode ? 'dark-mode' : ''}`}>
                <p>{item.question}</p>
            </div>
            <div className={`answer-content ${isDarkMode ? 'dark-mode' : ''}`}>
                {/* Only show "Answer" heading if there's content to display */}
                {(queryResponseContent || statisticsElements) && <h4>Answer</h4>}

                {/* Render Markdown content */}
                {queryResponseContent && (
                    <div className="markdown-output">
                        <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                                // Wrap tables so wide ones scroll inside the box
                                // instead of pushing the layout wider.
                                table: ({ node, ...props }) => (
                                    <div className="md-table-scroll">
                                        <table {...props} />
                                    </div>
                                ),
                                // Ensure charts and images scale responsively inside a dedicated wrapper with zoom preview
                                img: ({ node, alt, src, ...props }) => (
                                    <figure className="chart-wrapper">
                                        <div className="chart-banner-tag">
                                            <span className="chart-badge-title">Executive Chart</span>
                                            <span className="chart-badge-action">Click to expand</span>
                                        </div>
                                        <img
                                            src={src}
                                            alt={alt || "Executive Analytics Chart"}
                                            className="chart-img zoomable-chart"
                                            loading="lazy"
                                            onClick={() => setPreviewImg({ src, alt: alt || "Executive Analytics Chart" })}
                                            {...props}
                                        />
                                    </figure>
                                ),
                                // Render chart download links as interactive styled action buttons
                                a: ({ node, href, children, ...props }) => {
                                    const isChartDownload = href && (href.includes('/auth/api/analysis/chart/') || href.includes('save=1'));
                                    if (isChartDownload) {
                                        return (
                                            <a
                                                href={href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="chart-download-btn"
                                                download
                                                {...props}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle' }}>
                                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                    <polyline points="7 10 12 15 17 10" />
                                                    <line x1="12" y1="15" x2="12" y2="3" />
                                                </svg>
                                                <span>{children || "Download High-Res PNG"}</span>
                                            </a>
                                        );
                                    }
                                    return (
                                        <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                                            {children}
                                        </a>
                                    );
                                },
                            }}
                        >{queryResponseContent}</ReactMarkdown>
                    </div>
                )}

                {/* Render statistics */}
                {statisticsElements}

                {/* Lightbox Modal for Executive Chart Expansion */}
                {previewImg && (
                    <div className="chart-lightbox-overlay" onClick={() => setPreviewImg(null)}>
                        <div className="chart-lightbox-card" onClick={(e) => e.stopPropagation()}>
                            <div className="lightbox-header">
                                <h4>{previewImg.alt}</h4>
                                <button className="lightbox-close-btn" onClick={() => setPreviewImg(null)}>×</button>
                            </div>
                            <div className="lightbox-body">
                                <img src={previewImg.src} alt={previewImg.alt} className="lightbox-full-img" />
                            </div>
                            <div className="lightbox-footer">
                                <a
                                    href={previewImg.src + (previewImg.src.includes('?') ? '&' : '?') + 'save=1'}
                                    className="chart-download-btn"
                                    download
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Download High-Res PNG
                                </a>
                                <button className="lightbox-dismiss-btn" onClick={() => setPreviewImg(null)}>
                                    Close Preview
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};



export default ResponseItem;